import React, { createContext, useContext, useEffect, useState } from "react";

export type Theme = "light" | "dark";
export const THEME_STORAGE_KEY = "smartdoc-theme";

const getSavedTheme = (): Theme | null => {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
};

export const resolveTheme = (): Theme => {
  const saved = getSavedTheme();
  if (saved) return saved;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const applyTheme = (theme: Theme) => {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
};

export const initializeTheme = () => applyTheme(resolveTheme());

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(resolveTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const preference = window.matchMedia?.("(prefers-color-scheme: dark)");
    const onSystemChange = () => {
      if (getSavedTheme() === null) setTheme(resolveTheme());
    };
    const onStorageChange = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) setTheme(resolveTheme());
    };

    preference?.addEventListener?.("change", onSystemChange);
    window.addEventListener("storage", onStorageChange);
    return () => {
      preference?.removeEventListener?.("change", onSystemChange);
      window.removeEventListener("storage", onStorageChange);
    };
  }, []);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Theme still changes for this tab when storage is unavailable.
    }
    setTheme(next);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};
